/**
 * src/plugins/commerce/GarageShopDrawer.tsx
 *
 * Creator Garage Shops & Live Product Drops.
 * - Micro-storefront attached directly to channels.
 * - Supports physical goods, digital downloads, and consulting time.
 * - 1-Click Buy via Non-Custodial Escrow.
 */

import React, { useState } from 'react';
import { X, ShoppingBag, ShieldCheck, Download, Package, Clock, Plus, Check } from 'lucide-react';

export interface GarageProduct {
  id: string;
  channelId: string;
  creatorId: string;
  creatorHandle: string;
  title: string;
  description: string;
  price: {
    amount: number;
    currency: 'USD' | 'TOKEN';
  };
  inventoryType: 'physical' | 'digital_download' | 'service_consulting';
  stockCount?: number;
  previewMedia: {
    thumbnailUrl: string;
    liveDemoVideoUrl?: string;
  };
  escrowProtected: boolean;
}

export const SEEDED_GARAGE_PRODUCTS: GarageProduct[] = [
  {
    id: 'prod_synth_01',
    channelId: 'chan_showcase',
    creatorId: 'p_creator1',
    creatorHandle: 'nadiachen.herx',
    title: 'Analog Lo-Fi Synth Sample Pack (Vol. 1)',
    description: '120 royalty-free stems, analog drum loops, and ambient pads recorded on reel-to-reel tape.',
    price: { amount: 25, currency: 'USD' },
    inventoryType: 'digital_download',
    previewMedia: { thumbnailUrl: '🎹' },
    escrowProtected: true,
  },
  {
    id: 'prod_mech_02',
    channelId: 'chan_node_runners',
    creatorId: 'p_depin',
    creatorHandle: 'relay01.herx',
    title: 'ESP32-S3 Mesh Bridge 3D-Printed Enclosure',
    description: 'Custom ventilated snap-fit enclosure with external SMA antenna cutout and USB-C strain relief.',
    price: { amount: 15, currency: 'TOKEN' },
    inventoryType: 'physical',
    stockCount: 8,
    previewMedia: { thumbnailUrl: '📦' },
    escrowProtected: true,
  },
  {
    id: 'prod_consult_03',
    channelId: 'chan_builders',
    creatorId: 'p_admin',
    creatorHandle: 'hercules.herx',
    title: '1-on-1 Sovereign DePIN Architecture Advisory',
    description: '60-minute private technical consultation covering edge storage sharding, WebRTC fan-out, and DR.',
    price: { amount: 50, currency: 'TOKEN' },
    inventoryType: 'service_consulting',
    previewMedia: { thumbnailUrl: '⚡' },
    escrowProtected: true,
  },
];

export interface GarageShopDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  channelId?: string;
  channelTitle?: string;
  onPinToStream?: (product: GarageProduct) => void;
}

export const GarageShopDrawer: React.FC<GarageShopDrawerProps> = ({
  isOpen,
  onClose,
  channelId,
  channelTitle = 'Current Channel',
  onPinToStream,
}) => {
  const [products, setProducts] = useState<GarageProduct[]>(SEEDED_GARAGE_PRODUCTS);
  const [isAddingItem, setIsAddingItem] = useState<boolean>(false);
  const [purchasedId, setPurchasedId] = useState<string | null>(null);

  // Form states
  const [newTitle, setNewTitle] = useState('');
  const [newPrice, setNewPrice] = useState('20');
  const [newType, setNewType] = useState<GarageProduct['inventoryType']>('digital_download');

  if (!isOpen) return null;

  const handleBuy = (prod: GarageProduct) => {
    setPurchasedId(prod.id);
    setTimeout(() => setPurchasedId(null), 3000);
  };

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    const newProd: GarageProduct = {
      id: `prod_${Date.now()}`,
      channelId: channelId || 'chan_general',
      creatorId: 'current_user',
      creatorHandle: 'pioneer.herx',
      title: newTitle.trim(),
      description: 'Newly listed community product.',
      price: { amount: Number(newPrice) || 10, currency: 'USD' },
      inventoryType: newType,
      previewMedia: { thumbnailUrl: newType === 'physical' ? '📦' : newType === 'digital_download' ? '💾' : '🤝' },
      escrowProtected: true,
    };

    setProducts([newProd, ...products]);
    setIsAddingItem(false);
    setNewTitle('');
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 10000,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          height: '100%',
          background: '#0c101a',
          borderLeft: '1px solid #1e293b',
          display: 'flex',
          flexDirection: 'column',
          color: '#f8fafc',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, #111726 0%, #0c101a 100%)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShoppingBag size={16} color="#ffffff" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Creator Garage Shop</div>
              <div style={{ fontSize: 11, color: '#34d399', fontWeight: 600 }}>
                {channelTitle} &bull; Escrow-Protected P2P
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Action sub-bar */}
        <div
          style={{
            padding: '10px 22px',
            background: '#070a10',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{products.length} Active Items</span>
          <button
            type="button"
            onClick={() => setIsAddingItem(!isAddingItem)}
            style={{
              background: 'rgba(56, 189, 248, 0.1)',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              color: '#38bdf8',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Plus size={12} /> {isAddingItem ? 'Cancel' : 'List Item'}
          </button>
        </div>

        {/* Add Product Form */}
        {isAddingItem && (
          <form onSubmit={handleAddItem} style={{ padding: 16, background: '#111726', borderBottom: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#38bdf8' }}>List Item in Garage Shop</div>
            <input
              type="text"
              placeholder="Item Title (e.g. 3D Model, Preset, Consulting)"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              required
              style={{ background: '#070a10', border: '1px solid #1e293b', borderRadius: 6, padding: '8px 10px', color: '#f8fafc', fontSize: 12 }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input
                type="number"
                placeholder="Price"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                required
                style={{ background: '#070a10', border: '1px solid #1e293b', borderRadius: 6, padding: '8px 10px', color: '#f8fafc', fontSize: 12 }}
              />
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as any)}
                style={{ background: '#070a10', border: '1px solid #1e293b', borderRadius: 6, padding: '8px 10px', color: '#f8fafc', fontSize: 12 }}
              >
                <option value="digital_download">Digital Download</option>
                <option value="physical">Physical Item</option>
                <option value="service_consulting">1-on-1 Consulting</option>
              </select>
            </div>
            <button
              type="submit"
              style={{ background: '#10b981', border: 'none', borderRadius: 6, padding: '8px 12px', color: '#ffffff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
            >
              Publish Listing
            </button>
          </form>
        )}

        {/* Product Items List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {products.map((prod) => {
            const isJustPurchased = purchasedId === prod.id;

            return (
              <div
                key={prod.id}
                style={{
                  background: '#0f172a',
                  border: '1px solid #1e293b',
                  borderRadius: 14,
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: '#162032',
                      border: '1px solid #334155',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 22,
                      flexShrink: 0,
                    }}
                  >
                    {prod.previewMedia.thumbnailUrl}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#f8fafc' }}>
                      {prod.title}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      by @{prod.creatorHandle} &bull; {prod.inventoryType.replace('_', ' ')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontWeight: 800, fontSize: 15, color: '#10b981' }}>
                      {prod.price.currency === 'USD' ? `$${prod.price.amount}` : `${prod.price.amount} TOKENS`}
                    </span>
                  </div>
                </div>

                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.4 }}>
                  {prod.description}
                </div>

                <div
                  style={{
                    borderTop: '1px solid #1e293b',
                    paddingTop: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#34d399', fontWeight: 600 }}>
                    <ShieldCheck size={13} /> Non-Custodial Escrow
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    {onPinToStream && (
                      <button
                        type="button"
                        onClick={() => onPinToStream(prod)}
                        style={{
                          background: 'rgba(56, 189, 248, 0.1)',
                          border: '1px solid rgba(56, 189, 248, 0.3)',
                          color: '#38bdf8',
                          borderRadius: 6,
                          padding: '6px 10px',
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Pin to Stream
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => handleBuy(prod)}
                      style={{
                        background: isJustPurchased ? '#10b981' : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        border: 'none',
                        borderRadius: 6,
                        color: '#ffffff',
                        padding: '6px 14px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      {isJustPurchased ? <Check size={13} /> : null}
                      {isJustPurchased ? 'Escrow Locked!' : 'Buy via Escrow'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
